export const SCENARIOS = [
  {
    id: '2bd379b2-a017-49d7-889d-10162c45af04',
    title: 'Program Manager Meeting',
    description:
      'Collaborate with Jamie to explore how donor-funded program ideas can align with existing priorities, capacity, and values.',
    gender: 'MALE',
    goal: 'Jamie articulates specific program considerations and signals conditional alignment or next steps for continued planning.',
    duration: 300,
    pinned: false,
    createdAt: '2025-06-16T13:07:12.257Z',
    updatedAt: '2025-06-16T13:07:12.257Z',
    donorScenarioPromptId: 'bd41349e-4bf1-49ef-b6a9-de4bd131870c',
    instructions:
      'You are simulating a realistic program coordinator avatar named Jamie, engaging in a conversation with a fundraiser who is exploring how to align the scope and implementation of a new donor-funded initiative with existing program capacity and priorities. You have been with the organization for five years, are deeply embedded in the direct work, and have a strong sense of program integrity. While you recognize the importance of fundraising, you\'re protective of how external influence might affect delivery, workload, and long-term mission fit.\nYou\'re open to a strategic conversation—as long as it respects your program expertise and honors the nuanced tone and expectations you bring to the table, adjusted explicitly by the selected difficulty.\nThe session difficulty is set to: {{session_level}}\nPersona and Tone Adjusted by Difficulty:\nEasy sessions: Collaborative and pragmatic. You ask 2–3 curiosity-based questions around how the fundraiser envisions donor alignment. You express supportive interest in co-developing strategy, with a clear desire to make it work.\nMedium sessions: Cautiously diplomatic. You raise 4–5 pointed but constructive questions about how fundraising expectations may affect program flow, capacity, and reporting. You\'re not obstructive—but you are clearly protecting core program integrity.\nHard sessions: Skeptical and boundary-setting. You raise 6–7 detailed challenges around mission drift, program burden, and donor overreach. You allow minimal space for assumptions, requiring evidence and clarity at every turn. You communicate the weight of prior disappointments or capacity strains.\nConversational Structure for All Levels:\nOpening: Start with a warm but grounded tone, acknowledging the fundraiser\'s intentions while expressing your own program-centered perspective.\nChallenges You Raise:\nConcerns about mission drift or program misalignment\nLimits of staff capacity and hidden operational costs\nRisk of donor-driven metrics distorting priorities\nDemand for clarity on sustainability and post-funding support\nSpecific Prompts/Questions You Might Ask:\n"How flexible is the donor on timeline or outcomes?"\n"What happens if we can\'t sustain this work after year one?"\n"Who\'s managing the reporting expectations—us or development?"\n"What\'s the plan if the donor starts pushing for different deliverables?"\nClosing: If the fundraiser demonstrates authentic understanding, shared problem-solving, and a willingness to advocate internally on your behalf, you close with professional warmth and a tentative commitment to continued collaboration. If not, you remain courteous but clearly reserved, signaling the need for further internal alignment.',
    openingSentence:
      "Ya I'm always glad when we get to talk about how these funding ideas connect with what's actually happening on the ground. So where should we start?"
  },
  {
    id: 'c79ae570-beb6-49b7-864f-61eb047ced80',
    title: 'Energizing Donor Conversation',
    description: 'Passionate donor wants to connect emotionally. Share your story and inspire.',
    gender: 'MALE',
    'goal':
      'Donor affirms deeper commitment (e.g., signs up for a tour, pledges additional support, offers to advocate).',
    duration: 300,
    pinned: false,
    createdAt: '2025-05-14T18:54:54.895Z',
    updatedAt: '2025-05-14T18:54:54.895Z',
    donorScenarioPromptId: 'c861ff03-c372-4aa3-b47b-869c13cdc648',
    instructions:
      'You are a donor avatar named Taylor, engaged in a conversation with a fundraiser (the User).\n\nYour only role is to respond as a donor, never as the fundraiser.\n\nYou do not solicit or act as someone fundraising.\n\nThe User (Fundraiser) leads the conversation, and you react and ask questions based on your persona characteristics.\n\nYou are simulating a donor avatar named Taylor, who is genuinely enthusiastic and curious about a fundraiser’s organization. Your goal in this conversation is to engage warmly, express authentic excitement, and encourage the fundraiser to share deeply personal stories about their motivations, passions, and the meaningful impact of their work.\n\nPersona and Tone – Always:\n\nWarm, friendly, enthusiastic, and highly encouraging.\nGenuinely interested in hearing personal stories, motivations, and heartfelt connections to the organization’s mission.\nSupportive and positive—making the fundraiser feel valued, heard, and inspired.\nClearly signaling openness and eagerness to financially support the cause, emphasizing the alignment of values and passion.\n\nKey Conversation Goals:\n\nEncourage personal storytelling from the fundraiser about:\nWhy they joined or started working with the organization.\nPersonal stories or experiences that highlight their deep care and commitment.\nSpecific moments that made them proud, inspired, or motivated in their work.\n\nExpress genuine excitement and affirmations such as:\n"That\'s incredibly inspiring!"\n"I can really feel your passion and commitment."\n"Hearing your story makes me even more eager to support this work."\n\nActively ask open-ended, encouraging questions such as:\n"What first drew you to this mission?"\n"Can you share a moment when you felt especially proud of your impact?"\n"What keeps your passion for this cause burning strong?"\n\nDonor Interest & Support Signals:\n\nClearly express a willingness and excitement about contributing financially, using statements like:\n"I definitely want to support your amazing work."\n"This feels like exactly the kind of impact I want to be part of."\n"I\'m excited to hear how I can help take this incredible work even further."\n\nConversation Impact Goal:\n\nThe fundraiser should leave this conversation feeling energized, deeply connected to their mission, appreciated, and optimistic about their ability to inspire and engage donors through authentic storytelling and meaningful dialogue.',
    openingSentence: 'Hello, how are you doing?'
  }
];

export const DONOR_SENTIMENT = {
  summary:
    "The donor showed increased interest and alignment with the project's goals, reflecting a positive shift in sentiment.",
  indicators: [
    { name: 'Impact Belief', score: 1 },
    { name: 'Empathy', score: 1 },
    { name: 'Connection', score: 1 },
    { name: 'Alignment', score: 1 }
  ]
};

export const SKILLS = [
  {
    name: 'Communication & Delivery Skills',
    summary: 'Your communication was clear but occasionally lacked engagement prompts.',
    indicators: [
      {
        name: 'Confidence',
        importance: "Confidence in communication reassures the donor of the project's legitimacy and your capability.",
        guidance:
          'Maintain this confidence but pair it with engagement techniques to make the conversation more interactive.',
        observation:
          "You confidently updated the donor about the project's progress, which shows your command over the project details.",
        score: 75
      },
      {
        name: 'Structure, Flow, Transitions',
        importance:
          "Smooth transitions help maintain the donor's interest and understanding throughout the conversation.",
        guidance: 'Work on linking statements between topics to create a more cohesive narrative flow.',
        observation:
          'You structured the update well but transitions between topics felt abrupt, which might confuse or disengage the donor.',
        score: 50
      },
      {
        name: 'Balance & Quality of Dialogue',
        importance:
          "Effective dialogue involves both speaking and listening, creating a balanced conversation that values the donor's input.",
        guidance:
          "Incorporate more open-ended questions to invite the donor's thoughts and feelings about the updates.",
        observation: 'You provided detailed updates, yet the dialogue was mostly one-sided with limited donor input.',
        score: 50
      },
      {
        name: 'Defined Next Step',
        importance:
          'Defining next steps provides clear direction for the relationship and keeps the donor engaged with the project.',
        guidance: 'End conversations by summarizing the discussion and proposing a clear next action for the donor.',
        observation:
          'You mentioned ongoing project developments but did not clearly define the next steps or how the donor could be involved.',
        score: 75
      }
    ]
  },
  {
    name: 'Fundraising-Specific Skills',
    summary: 'Your fundraising approach was informative but lacked direct engagement strategies.',
    indicators: [
      {
        name: 'Clarity of Purpose and Goal Alignment',
        importance:
          "Clear purpose and alignment with the donor's interests increase the likelihood of continued support.",
        guidance: 'Continue to highlight how the project aligns with their values and interests in future discussions.',
        observation:
          "You clearly articulated the project's purpose and aligned it with the donor's past support, reinforcing their decision to give.",
        score: 75
      },
      {
        name: "Relevance to Funder's Interests",
        importance:
          "Tailoring the conversation to the donor's specific interests fosters deeper connection and engagement.",
        guidance:
          "Keep the donor's interests at the forefront of the conversation to maintain their engagement and support.",
        observation: "You effectively connected the project's goals with the donor's interest in educational equity.",
        score: 75
      },
      {
        name: 'Ask Effectiveness',
        importance: 'Effective asks are direct and tailored, making it clear what is needed from the donor next.',
        guidance: 'Be more explicit in your asks, ensuring they are specific, timely, and relevant to the donor.',
        observation:
          'You discussed project needs implicitly but did not make a clear or direct ask for further support or involvement.',
        score: 50
      },
      {
        name: 'Vision & Value Articulation',
        importance:
          'A strong vision helps the donor see the impact of their contributions and inspires continued support.',
        guidance:
          "Continue to refine and communicate the project's vision and its impact to keep the donor motivated and involved.",
        observation:
          "You articulated a compelling vision for the project, which resonates with the donor's values of supporting educational opportunities.",
        score: 75
      }
    ]
  },
  {
    name: 'Relationship Building',
    summary:
      'You reconnected with the donor effectively, though more personal engagement could strengthen the relationship.',
    indicators: [
      {
        name: 'Donor Engagement Score',
        importance:
          'High donor engagement is crucial for maintaining long-term relationships and ensuring ongoing support.',
        guidance:
          'Increase engagement by encouraging the donor to share their thoughts and feelings about the project updates.',
        observation:
          'You re-engaged the donor by updating them on the project, but there was limited interactive dialogue.',
        score: 50
      },
      {
        name: 'Relational Depth Score',
        importance:
          'Deepening personal connections with donors enhances trust and loyalty, making them more likely to support over the long term.',
        guidance:
          "Personalize your interactions more by recalling past conversations, acknowledging the donor's personal interests, and expressing genuine appreciation for their support.",
        observation:
          'You maintained a professional tone and provided updates, but missed opportunities to deepen the personal connection.',
        score: 50
      },
      {
        name: 'Trust Building Effectiveness',
        importance:
          'Trust is the foundation of any strong donor relationship, leading to more open and honest conversations and increased support.',
        guidance:
          'Continue to be transparent and honest in all communications to strengthen trust and encourage open dialogue.',
        observation:
          "You maintained transparency about the project's progress, which helps build trust with the donor.",
        score: 75
      },
      {
        name: 'Active Listening',
        importance:
          'Active listening shows donors that their opinions are valued and considered, strengthening the relationship.',
        guidance:
          "Practice active listening by summarizing the donor's points to confirm understanding and asking follow-up questions to demonstrate interest.",
        observation:
          "You listened to the donor's initial reactions but did not actively seek their input throughout the conversation.",
        score: 50
      }
    ]
  },
  {
    name: 'Strategic Thinking',
    summary: 'Your strategic approach was evident but could be more donor-centric.',
    indicators: [
      {
        name: 'Insight Gathering',
        importance:
          "Gathering insights about the donor's current situation can lead to more tailored and effective engagement strategies.",
        guidance:
          "Ask open-ended questions to uncover the donor's current interests, concerns, and potential for further involvement.",
        observation:
          "You gathered some insights based on the donor's past contributions but did not explore their current interests or capacity.",
        score: 50
      },
      {
        name: 'Framing for Mission–Funder Alignment',
        importance:
          "Properly framing the mission to align with the donor's values enhances their connection to the cause and likelihood of continued support.",
        guidance:
          'Continue to frame discussions around how the project aligns with their values and how their support makes a difference.',
        observation:
          "You effectively framed the project in a way that aligns with the donor's past support for educational initiatives.",
        score: 75
      },
      {
        name: 'Navigating Tension or Pushback',
        importance:
          'Effectively managing tension or pushback maintains a positive relationship and open communication lines.',
        guidance:
          'Maintain this calm demeanor in all interactions, especially when addressing potential concerns or objections from the donor.',
        observation: 'There was little tension in this conversation, and you handled all discussions smoothly.',
        score: 100
      },
      {
        name: 'Adaptability and Decision-Making Under Pressure',
        importance:
          'Being adaptable and making informed decisions quickly under pressure shows competence and reliability, strengthening donor confidence.',
        guidance:
          'Continue to prepare thoroughly for meetings, anticipating potential questions and having clear, informative responses ready.',
        observation:
          "You adapted well to the donor's questions about the project's progress, providing clear and concise updates.",
        score: 75
      }
    ]
  }
];
